import type { Exercise } from "../types";

export const avanzadoExercises: Exercise[] = [
  {
    id: "av-01",
    levelId: "avanzado",
    order: 1,
    title: "Escalas Mayores: La Formula",
    subtitle: "Tono-Tono-Semitono-Tono-Tono-Tono-Semitono: la clave de la armonia",
    category: "escalas",
    difficulty: "dificil",
    type: "sequence",
    methodRef: "Cap. XIV - Escalas mayores (Ej. 57-60)",
    description:
      "La escala mayor es la fundacion de toda la musica occidental. Su formula (TTSTTTS) se aplica desde cualquier nota. Este ejercicio te ensena a construirla en el mastil sin memorizar patrones de dedos.",
    instructions: [
      "Parte de una nota raiz en cualquier cuerda.",
      "Aplica la formula: T-T-ST-T-T-T-ST (T=2 trastes, ST=1 traste).",
      "Sigue la secuencia cromatica ascendente.",
    ],
    steps: [
      { id: 1, instruction: "Do Mayor en cuerda 5: Do (traste 3)", positions: [{ string: 5, fret: 3 }] },
      { id: 2, instruction: "Re (traste 5) - Tono", positions: [{ string: 5, fret: 5 }] },
      { id: 3, instruction: "Mi (traste 7) - Tono", positions: [{ string: 5, fret: 7 }] },
      { id: 4, instruction: "Fa (traste 8) - Semitono", positions: [{ string: 5, fret: 8 }] },
      { id: 5, instruction: "Sol (traste 10) - Tono", positions: [{ string: 5, fret: 10 }] },
      { id: 6, instruction: "La (traste 12) - Tono", positions: [{ string: 5, fret: 12 }] },
      { id: 7, instruction: "Si (traste 14) - Tono", positions: [{ string: 5, fret: 14 }] },
      { id: 8, instruction: "Do (cuerda 4, traste 3) - Semitono (octava)", positions: [{ string: 4, fret: 3 }] },
    ],
    professorHint:
      'No memorices posiciones: comprende la FORMULA. T-T-ST-T-T-T-ST es la misma desde cualquier nota. Es la diferencia entre "saber escalas" y "entender escalas".',
    professorNote:
      "El Capitulo XIV del metodo chileno tiene 4 ejercicios de escalas mayores. El concepto de equisonos (Cap. XIII) es prerequisito: para que la escala suene uniforme, debes sustituir cuerdas al aire por sus equisonos.",
    timeEstimate: 8,
    stringsNeeded: [4, 5],
    fretRange: [0, 15],
  },
  {
    id: "av-02",
    levelId: "avanzado",
    order: 2,
    title: "La Ceja: El Gran Salto",
    subtitle: "Dominar la cejilla es pasar de principiante a guitarrista",
    category: "digitacion",
    difficulty: "dificil",
    type: "practice",
    methodRef: "Cap. XV - Ceja o cejilla (Ej. 61)",
    description:
      'La ceja (cejilla) usa el dedo indice como una "cejilla" que presiona todas las cuerdas a la vez. Es la tecnica que abre el mastil completo: sin ella, estas limitado a los acordes abiertos.',
    instructions: [
      "Coloca el dedo 1 plano sobre todos los trastes de una posicion.",
      "Presiona uniformemente para que todas las cuerdas suenen.",
      "Practica primero en los trastes altos (mas facil) y baja gradualmente.",
    ],
    steps: [
      {
        id: 1,
        instruction: "Ceja en traste V: dedo 1 plano sobre todas las cuerdas",
        positions: [
          { string: 6, fret: 5 },
          { string: 5, fret: 5 },
          { string: 4, fret: 5 },
          { string: 3, fret: 5 },
          { string: 2, fret: 5 },
          { string: 1, fret: 5 },
        ],
      },
      { id: 2, instruction: "Verifica: toca cada cuerda individualmente. Suenan todas?", positions: [{ string: 6, fret: 5 }] },
      {
        id: 3,
        instruction: "Anade un acorde: ceja en V + dedo 3 en traste VII cuerda 5",
        positions: [
          { string: 6, fret: 5 },
          { string: 5, fret: 7 },
          { string: 4, fret: 5 },
          { string: 3, fret: 5 },
          { string: 2, fret: 5 },
          { string: 1, fret: 5 },
        ],
      },
      {
        id: 4,
        instruction: "Este es La Mayor en quinta posicion. Practica el cambio de acordes.",
        positions: [{ string: 5, fret: 7 }, { string: 4, fret: 7 }],
      },
    ],
    professorHint:
      "El truco de la ceja NO es fuerza: es POSICION. El dedo indice debe estar lo mas cerca del traste metalico como sea posible, no en medio entre dos trastes.",
    professorNote:
      'El Capitulo XV se dedica exclusivamente a la ceja. Es el "gran filtro" del aprendizaje: muchos abandonan aqui. Quien la domina, tiene el mastil entero a su disposicion.',
    timeEstimate: 8,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 8],
  },
  {
    id: "av-03",
    levelId: "avanzado",
    order: 3,
    title: "Arpegios con el Pulgar",
    subtitle: "El pulgar como plectro: arpegios alternados rapidos",
    category: "arpegios",
    difficulty: "dificil",
    type: "sequence",
    methodRef: "Cap. XVI - Arpegios (2a parte), Ej. 62-64",
    description:
      'A diferencia de los arpegios sin apoyo del nivel intermedio, aqui el pulgar "alterna" con i-m-a para crear patrones ritmicos complejos. Es la tecnica de los estudios clasicos de Tarrega y Aguado.',
    instructions: [
      "El pulgar toca los bajos en un patron estable.",
      "i-m-a tocan las cuerdas agudas en contratiempo.",
      "El resultado es un flujo continuo de 4/4 o 3/4.",
    ],
    steps: [
      { id: 1, instruction: "Patron p-i-m-a: pulgar en cuerda 5 (La)", positions: [{ string: 5, fret: 0 }] },
      { id: 2, instruction: "Indice en cuerda 3 (Mi)", positions: [{ string: 3, fret: 2 }] },
      { id: 3, instruction: "Medio en cuerda 2 (La)", positions: [{ string: 2, fret: 2 }] },
      { id: 4, instruction: "Anular en cuerda 1 (Do#)", positions: [{ string: 1, fret: 2 }] },
      {
        id: 5,
        instruction: "Repite el ciclo p-i-m-a sin interrupcion",
        positions: [{ string: 5, fret: 0 }, { string: 3, fret: 2 }, { string: 2, fret: 2 }, { string: 1, fret: 2 }],
      },
    ],
    professorHint:
      "El patron p-i-m-a es el motor del arpegio clasico. Practicalo LENTO pero CONSTANTE: un metronomo al principio, luego sube 2 BPM cada dia.",
    professorNote:
      "El Capitulo XVI tiene 3 ejercicios de arpegios con pulgar. Es la tecnica que Eugenia Rodriguez utiliza en sus estudios originales del metodo.",
    timeEstimate: 8,
    stringsNeeded: [1, 2, 3, 5],
    fretRange: [0, 3],
  },
  {
    id: "av-04",
    levelId: "avanzado",
    order: 4,
    title: "Acordes con Ceja: La Quinta Posicion",
    subtitle: "Moverse libremente por el mastil usando la cejilla",
    category: "teoria",
    difficulty: "dificil",
    type: "click",
    methodRef: "Cap. XV + XVI - Consolidacion",
    description:
      "Con la ceja en la quinta posicion puedes tocar acordes de La, Re, Sol, Mi, Si. Este ejercicio te entrena a ver la cejilla como una cejilla movil que transporta los acordes abiertos a cualquier tonalidad.",
    instructions: [
      "Se te indicara un acorde y una posicion.",
      "Construye el acorde usando la ceja como base.",
      "Identifica las notas que forman el acorde.",
    ],
    steps: [
      {
        id: 1,
        instruction: "La Mayor con ceja en traste V: identifica las formulaciones correctas",
        options: [
          { label: "La - Mi - La", value: "a", correct: true },
          { label: "La - Do# - Mi", value: "b", correct: true },
          { label: "La - Re - Mi", value: "c", correct: false },
          { label: "Sol - Si - Re", value: "d", correct: false },
        ],
      },
      {
        id: 2,
        instruction: "Re Mayor con ceja en traste X: que notas lo forman?",
        options: [
          { label: "Re - Fa# - La", value: "a", correct: true },
          { label: "Re - Fa - La", value: "b", correct: false },
          { label: "Re - Fa# - La - Re", value: "c", correct: true },
          { label: "Mi - Sol# - Si", value: "d", correct: false },
        ],
      },
      {
        id: 3,
        instruction: "Mi Mayor con ceja en traste VII: marca la forma en el mastil",
        positions: [
          { string: 6, fret: 7 },
          { string: 5, fret: 9 },
          { string: 4, fret: 9 },
          { string: 3, fret: 8 },
          { string: 2, fret: 7 },
          { string: 1, fret: 7 },
        ],
        answers: ["Mi", "Si", "Mi", "Sol#", "Si", "Mi"],
      },
    ],
    professorHint:
      "La ceja en traste V transforma el acorde abierto de La. Mueve la ceja: traste VII = Si, traste VIII = Do, etc. Es la regla del transporte.",
    professorNote:
      "Este ejercicio combina los Capitulos XV (ceja) y XVI (arpegios). La ceja sin arpegios es solo un acorde; juntos forman el acompañamiento completo.",
    timeEstimate: 8,
    stringsNeeded: [1, 2, 3, 4, 5, 6],
    fretRange: [0, 12],
  },
];
